import { useEffect, useRef } from 'react';
import type { MessageBoxMode } from './MessageBoxMode';
import type { MessageBoxTone } from './MessageBoxTone';

export type MessageBoxProps = {
  open: boolean;
  mode?: MessageBoxMode;
  title?: string;
  message: string;
  details?: string[];
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: MessageBoxTone;
  onConfirm: () => void;
  onCancel: () => void;
};

/** Caixa de mensagem modal padrão do sistema (substitui window.confirm / window.alert). */
export function MessageBox({
  open,
  mode = 'confirm',
  title,
  message,
  details,
  confirmLabel,
  cancelLabel = 'Cancelar',
  tone = 'default',
  onConfirm,
  onCancel,
}: MessageBoxProps) {
  const primaryRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const isAlert = mode === 'alert';
  const resolvedTitle = title ?? (isAlert ? 'Aviso' : 'Confirmar ação');
  const resolvedConfirmLabel = confirmLabel ?? (isAlert ? 'OK' : 'Confirmar');

  useEffect(() => {
    if (!open) {
      return;
    }

    if (isAlert) {
      primaryRef.current?.focus();
    } else {
      cancelRef.current?.focus();
    }

    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        if (isAlert) {
          onConfirm();
        } else {
          onCancel();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, isAlert, onCancel, onConfirm]);

  if (!open) {
    return null;
  }

  const confirmClassName = tone === 'danger' ? 'erp-btn-danger' : 'erp-btn-primary';

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4"
      role="presentation"
      onClick={isAlert ? onConfirm : onCancel}
    >
      <div
        className="erp-card w-full max-w-lg"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="message-box-title"
        aria-describedby="message-box-message"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="erp-card-body space-y-4">
          <div className="space-y-2">
            <h2 id="message-box-title" className="erp-card-title">
              {resolvedTitle}
            </h2>
            <p id="message-box-message" className="whitespace-pre-line text-sm text-secondary-600">
              {message}
            </p>
            {details && details.length > 0 ? (
              <ul className="list-disc space-y-1 pl-5 text-sm text-secondary-600">
                {details.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : null}
          </div>

          <div className="erp-btn-actions">
            {isAlert ? (
              <button
                ref={primaryRef}
                type="button"
                className="erp-btn-primary"
                onClick={onConfirm}
              >
                {resolvedConfirmLabel}
              </button>
            ) : (
              <>
                <button
                  ref={cancelRef}
                  type="button"
                  className="erp-btn-secondary"
                  onClick={onCancel}
                >
                  {cancelLabel}
                </button>
                <button
                  ref={primaryRef}
                  type="button"
                  className={confirmClassName}
                  onClick={onConfirm}
                >
                  {resolvedConfirmLabel}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
